import React, { useMemo, useState, useEffect } from "react";
import FuseLoading from "components/shared-components/FuseLoading";
import { Link } from "react-router-dom";
import TailwindDataTable from "components/shared-components/taiwind/TailwindDataTable";

// ✅ Tailwind-only version of OrdersStatus
function OrdersStatus({ name }) {
  const statusMap = {
    "On pre-order": "bg-purple-600",
    "On pre-order (not paid)": "bg-purple-600",
    "On pre-order (paid)": "bg-purple-500",
    "Payment accepted": "bg-green-500",
    "Payment error": "bg-red-500",
    Delivered: "bg-green-700",
    Refunded: "bg-red-400",
    Canceled: "bg-red-600",
    Shipped: "bg-indigo-500",
    "Awaiting PayPal payment": "bg-blue-400",
    "Awaiting check payment": "bg-sky-500",
    "Awaiting bank wire payment": "bg-blue-500",
    "Remote payment accepted": "bg-emerald-600",
    "Preparing the order": "bg-amber-500",
  };

  const color = statusMap[name] || "bg-gray-400";
  return (
    <span
      className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-xs font-semibold text-white whitespace-nowrap ${color}`}
    >
      {name}
    </span>
  );
}

// ✅ Fake order generator
function makeOrder(data = {}) {
  return {
    id: data.id || `order-${Math.floor(Math.random() * 10000)}`,
    reference:
      data.reference || `ref-${Math.random().toString(36).substr(2, 6)}`,
    customer: `${data.firstName || "John"} ${data.lastName || "Doe"}`,
    total:
      typeof data.total === "number" ? data.total : Number(data.total) || 0,
    payment: data.payment?.method || data.payment || "Credit Card",
    status: data.statusName || "Payment accepted",
    date: data.date || new Date().toISOString().slice(0, 10),
  };
}

export default function OrdersTable() {
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // ✅ Load dummy data
  useEffect(() => {
    setTimeout(() => {
      const dummy = [
        makeOrder({
          id: 1,
          reference: "70d4d7d0",
          firstName: "Dollie",
          lastName: "Bullock",
          total: 73.31,
          payment: { method: "Credit Card" },
          statusName: "On pre-order",
          date: "2015/04/25",
        }),
        makeOrder({
          id: 2,
          reference: "2003479c",
          firstName: "Holmes",
          lastName: "Hines",
          total: 24.51,
          payment: { method: "Check" },
          statusName: "Payment accepted",
          date: "2015/11/07",
        }),
        makeOrder({
          id: 3,
          reference: "09f5443b",
          firstName: "Serena",
          lastName: "Glover",
          total: 87.17,
          payment: { method: "PayPal" },
          statusName: "Payment error",
          date: "2015/11/26",
        }),
        makeOrder({
          id: 4,
          reference: "960898d0",
          firstName: "Dianne",
          lastName: "Prince",
          total: 26.98,
          payment: { method: "Check" },
          statusName: "Delivered",
          date: "2016/03/02",
        }),
        makeOrder({
          id: 5,
          reference: "2d7f68de",
          firstName: "Frankie",
          lastName: "Hewitt",
          total: 12.97,
          payment: { method: "Bank-wire" },
          statusName: "Payment error",
          date: "2016/03/20",
        }),
        makeOrder({
          id: 6,
          reference: "c9917108",
          firstName: "Cole",
          lastName: "Holcomb",
          total: 30.96,
          payment: { method: "Check" },
          statusName: "Preparing the order",
          date: "2016/04/10",
        }),
        makeOrder({
          id: 7,
          reference: "7683b54d",
          firstName: "Merrill",
          lastName: "Richardson",
          total: 63.36,
          payment: { method: "PayPal" },
          statusName: "Awaiting check payment",
          date: "2016/04/22",
        }),
        makeOrder({
          id: 8,
          reference: "c1de0755",
          firstName: "Morgan",
          lastName: "Pitts",
          total: 45.74,
          payment: { method: "Credit Card" },
          statusName: "Shipped",
          date: "2016/05/01",
        }),
        makeOrder({
          id: 9,
          reference: "3504dbc6",
          firstName: "Krista",
          lastName: "Mathis",
          total: 15.31,
          payment: { method: "Bank-wire" },
          statusName: "Remote payment accepted",
          date: "2016/05/20",
        }),
        makeOrder({
          id: 10,
          reference: "a8bc5b17",
          firstName: "Hayden",
          lastName: "Fitzgerald",
          total: 20.97,
          payment: { method: "Credit Card" },
          statusName: "Delivered",
          date: "2016/05/25",
        }),
        makeOrder({
          id: 11,
          reference: "54ab8191",
          firstName: "Cotton",
          lastName: "Carlson",
          total: 72.3,
          payment: { method: "PayPal" },
          statusName: "On pre-order",
          date: "2016/06/02",
        }),
        makeOrder({
          id: 12,
          reference: "6919050",
          firstName: "Kaye",
          lastName: "Baldwin",
          total: 42.54,
          payment: { method: "Credit Card" },
          statusName: "Awaiting bank wire payment",
          date: "2016/06/11",
        }),
        makeOrder({
          id: 13,
          reference: "14e89f10",
          firstName: "Iva",
          lastName: "Clark",
          total: 97.49,
          payment: { method: "Check" },
          statusName: "Canceled",
          date: "2016/06/20",
        }),
        makeOrder({
          id: 14,
          reference: "d897564e",
          firstName: "Shauna",
          lastName: "Rosales",
          total: 16.95,
          payment: { method: "PayPal" },
          statusName: "Refunded",
          date: "2016/06/25",
        }),
        makeOrder({
          id: 15,
          reference: "1a803de2",
          firstName: "Carroll",
          lastName: "Dotson",
          total: 20.34,
          payment: { method: "Check" },
          statusName: "Awaiting check payment",
          date: "2016/07/01",
        }),
        makeOrder({
          id: 16,
          reference: "7d90eaa6",
          firstName: "Jeannie",
          lastName: "Reese",
          total: 10.64,
          payment: { method: "PayPal" },
          statusName: "On pre-order",
          date: "2016/07/04",
        }),
        makeOrder({
          id: 17,
          reference: "cf9b4bfc",
          firstName: "Dena",
          lastName: "Rowe",
          total: 27.06,
          payment: { method: "PayPal" },
          statusName: "Delivered",
          date: "2016/07/11",
        }),
        makeOrder({
          id: 18,
          reference: "07a938c4",
          firstName: "Blankenship",
          lastName: "Lynch",
          total: 91.5,
          payment: { method: "PayPal" },
          statusName: "Canceled",
          date: "2016/07/20",
        }),
        makeOrder({
          id: 19,
          reference: "d460f4ff",
          firstName: "Whitley",
          lastName: "McGee",
          total: 47.99,
          payment: { method: "PayPal" },
          statusName: "Refunded",
          date: "2016/07/29",
        }),
        makeOrder({
          id: 20,
          reference: "bda9464d",
          firstName: "Hood",
          lastName: "Hodges",
          total: 44.4,
          payment: { method: "PayPal" },
          statusName: "Awaiting bank wire payment",
          date: "2016/08/04",
        }),
      ];
      setOrders(dummy);
      setIsLoading(false);
    }, 300);
  }, []);

  const columns = useMemo(
    () => [
      {
        header: "Reference",
        accessorKey: "reference",
        Cell: ({ value, row }) => (
          <Link
            to={`/apps/e-commerce/orders/${row.id}`}
            className="text-redlight font-medium hover:underline"
          >
            {value}
          </Link>
        ),
      },
      { header: "Customer", accessorKey: "customer" },
      { header: "Total", accessorKey: "total" },
      { header: "Payment", accessorKey: "payment" },
      {
        header: "Status",
        accessorKey: "status",
        Cell: ({ value }) => <OrdersStatus name={value} />,
      },
      { header: "Date", accessorKey: "date" },
    ],
    []
  );

  if (isLoading) return <FuseLoading />;

  return (
    <div className="flex flex-col w-full">
      <TailwindDataTable columns={columns} data={orders} />
    </div>
  );
}

// import { useMemo, useState, useEffect } from "react";
// import DataTable from "components/shared-components/data-table/DataTable";
// import { ListItemIcon, MenuItem, Paper } from "@mui/material";
// import Button from "@mui/material/Button";
// import Typography from "@mui/material/Typography";
// import { Link } from "react-router-dom";
// import FuseSvgIcon from "components/shared-components/FuseSvgIcon";
// import FuseLoading from "components/shared-components/FuseLoading";

// // A dummy OrdersStatus component (replace with your real one)
// import React from "react";
// import TailwindDataTable from "components/shared-components/taiwind/TailwindDataTable";

// function OrdersStatus({ name }) {
//   const statusMap = {
//     "On pre-order": "bg-purple-600",
//     "On pre-order (not paid)": "bg-purple-600",
//     "On pre-order (paid)": "bg-purple-500",
//     "Payment accepted": "bg-green-500",
//     "Payment error": "bg-red-500",
//     Delivered: "bg-green-700",
//     Refunded: "bg-red-400",
//     Canceled: "bg-red-600",
//     Shipped: "bg-indigo-500",
//     "Awaiting PayPal payment": "bg-blue-400",
//     "Awaiting check payment": "bg-sky-500",
//     "Awaiting bank wire payment": "bg-blue-500",
//     "Remote payment accepted": "bg-emerald-600",
//     "Preparing the order": "bg-amber-500",
//   };

//   const color = statusMap[name] || "bg-gray-400";

//   return (
//     <span
//       className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-xs font-semibold text-white whitespace-nowrap ${color}`}
//     >
//       {name}
//     </span>
//   );
// }

// // Fake data generator for orders
// function makeOrder(data = {}) {
//   return {
//     id: data.id || `order-${Math.floor(Math.random() * 10000)}`,
//     reference:
//       data.reference || `ref-${Math.random().toString(36).substr(2, 6)}`,
//     customer: {
//       firstName: data.firstName || "John",
//       lastName: data.lastName || "Doe",
//     },
//     total:
//       typeof data.total === "number" ? data.total : Number(data.total) || 0,
//     payment: {
//       method: data.payment?.method || data.payment || "Credit Card", // ✅ accept string or object
//     },
//     status: data.status || [{ name: data.statusName || "Payment accepted" }],
//     date: data.date || new Date().toISOString().slice(0, 10),
//   };
// }

// function OrdersTable() {
//   const [orders, setOrders] = useState(null);
//   const [isLoading, setIsLoading] = useState(true);

//   // simulate fetch
//   useEffect(() => {
//     setTimeout(() => {
//       const dummy = [
//         makeOrder({
//           id: 1,
//           reference: "70d4d7d0",
//           firstName: "Dollie",
//           lastName: "Bullock",
//           total: 73.31,
//           payment: { method: "Credit Card" },
//           statusName: "On pre-order",
//           date: "2015/04/25",
//         }),
//         makeOrder({
//           id: 2,
//           reference: "2003479c",
//           firstName: "Holmes",
//           lastName: "Hines",
//           total: 24.51,
//           payment: { method: "Check" },
//           statusName: "Payment accepted",
//           date: "2015/11/07",
//         }),
//         makeOrder({
//           id: 3,
//           reference: "09f5443b",
//           firstName: "Serena",
//           lastName: "Glover",
//           total: 87.17,
//           payment: { method: "PayPal" },
//           statusName: "Payment error",
//           date: "2015/11/26",
//         }),
//         makeOrder({
//           id: 4,
//           reference: "960898d0",
//           firstName: "Dianne",
//           lastName: "Prince",
//           total: 26.98,
//           payment: { method: "Check" },
//           statusName: "Delivered",
//           date: "2016/03/02",
//         }),
//         makeOrder({
//           id: 5,
//           reference: "2d7f68de",
//           firstName: "Frankie",
//           lastName: "Hewitt",
//           total: 12.97,
//           payment: { method: "Bank-wire" },
//           statusName: "Payment error",
//           date: "2016/03/20",
//         }),
//         makeOrder({
//           id: 6,
//           reference: "c9917108",
//           firstName: "Cole",
//           lastName: "Holcomb",
//           total: 30.96,
//           payment: { method: "Check" },
//           statusName: "Preparing",
//           date: "2016/04/10",
//         }),
//         makeOrder({
//           id: 7,
//           reference: "7683b54d",
//           firstName: "Merrill",
//           lastName: "Richardson",
//           total: 63.36,
//           payment: { method: "PayPal" },
//           statusName: "Awaiting check payment",
//           date: "2016/04/22",
//         }),
//         makeOrder({
//           id: 8,
//           reference: "c1de0755",
//           firstName: "Morgan",
//           lastName: "Pitts",
//           total: 45.74,
//           payment: { method: "Credit Card" },
//           statusName: "Shipped",
//           date: "2016/05/01",
//         }),
//         makeOrder({
//           id: 9,
//           reference: "3504dbc6",
//           firstName: "Krista",
//           lastName: "Mathis",
//           total: 15.31,
//           payment: { method: "Bank-wire" },
//           statusName: "Remote payment accepted",
//           date: "2016/05/20",
//         }),
//         makeOrder({
//           id: 10,
//           reference: "a8bc5b17",
//           firstName: "Hayden",
//           lastName: "Fitzgerald",
//           total: 20.97,
//           payment: { method: "Credit Card" },
//           statusName: "Delivered",
//           date: "2016/05/25",
//         }),
//         makeOrder({
//           id: 11,
//           reference: "54ab8191",
//           firstName: "Cotton",
//           lastName: "Carlson",
//           total: 72.3,
//           payment: { method: "PayPal" },
//           statusName: "On pre-order",
//           date: "2016/06/02",
//         }),
//         makeOrder({
//           id: 12,
//           reference: "6919050",
//           firstName: "Kaye",
//           lastName: "Baldwin",
//           total: 42.54,
//           payment: { method: "Credit Card" },
//           statusName: "Awaiting bank wire payment",
//           date: "2016/06/11",
//         }),
//         makeOrder({
//           id: 13,
//           reference: "14e89f10",
//           firstName: "Iva",
//           lastName: "Clark",
//           total: 97.49,
//           payment: { method: "Check" },
//           statusName: "Canceled",
//           date: "2016/06/20",
//         }),
//         makeOrder({
//           id: 14,
//           reference: "d897564e",
//           firstName: "Shauna",
//           lastName: "Rosales",
//           total: 16.95,
//           payment: { method: "PayPal" },
//           statusName: "Refunded",
//           date: "2016/06/25",
//         }),
//         makeOrder({
//           id: 15,
//           reference: "1a803de2",
//           firstName: "Carroll",
//           lastName: "Dotson",
//           total: 20.34,
//           payment: { method: "Check" },
//           statusName: "Awaiting check payment",
//           date: "2016/07/01",
//         }),
//         makeOrder({
//           id: 16,
//           reference: "7d90eaa6",
//           firstName: "Jeannie",
//           lastName: "Reese",
//           total: 10.64,
//           payment: { method: "PayPal" },
//           statusName: "On pre-order",
//           date: "2016/07/04",
//         }),
//         makeOrder({
//           id: 17,
//           reference: "cf9b4bfc",
//           firstName: "Dena",
//           lastName: "Rowe",
//           total: 27.06,
//           payment: { method: "PayPal" },
//           statusName: "Delivered",
//           date: "2016/07/11",
//         }),
//         makeOrder({
//           id: 18,
//           reference: "07a938c4",
//           firstName: "Blankenship",
//           lastName: "Lynch",
//           total: 91.5,
//           payment: { method: "PayPal" },
//           statusName: "Canceled",
//           date: "2016/07/20",
//         }),
//         makeOrder({
//           id: 19,
//           reference: "d460f4ff",
//           firstName: "Whitley",
//           lastName: "McGee",
//           total: 47.99,
//           payment: { method: "PayPal" },
//           statusName: "Refunded",
//           date: "2016/07/29",
//         }),
//         makeOrder({
//           id: 20,
//           reference: "bda9464d",
//           firstName: "Hood",
//           lastName: "Hodges",
//           total: 44.4,
//           payment: { method: "PayPal" },
//           statusName: "Awaiting bank wire payment",
//           date: "2016/08/04",
//         }),
//       ];
//       setOrders(dummy);
//       setIsLoading(false);
//     }, 500);
//   }, []);

//   const removeOrders = (ids) => {
//     setOrders((prev = []) => prev.filter((o) => !ids.includes(o.id)));
//   };

//   const columns = useMemo(
//     () => [
//       {
//         accessorKey: "id",
//         header: "Id",
//         size: 32,
//       },
//       {
//         accessorKey: "reference",
//         header: "Reference",
//         size: 32,
//         Cell: ({ row }) => {
//           const orig = row.original || {};
//           return (
//             <Typography
//               component={Link}
//               to={`/apps/e-commerce/orders/${orig.id}`}
//               className="text-redlight"
//               color="text-redlight"
//               role="button"
//             >
//               {orig.reference}
//             </Typography>
//           );
//         },
//       },
//       {
//         id: "customer",
//         accessorFn: (row) =>
//           `${row.customer?.firstName ?? ""} ${
//             row.customer?.lastName ?? ""
//           }`.trim(),
//         header: "Customer",
//       },
//       {
//         id: "total",
//         accessorFn: (row) => `$${row.total?.toFixed(2) ?? "0.00"}`,
//         header: "Total",
//         size: 32,
//       },
//       {
//         id: "payment",
//         header: "Payment",
//         accessorFn: (row) => row.payment?.method ?? "",
//         size: 128,
//       },
//       {
//         id: "status",
//         accessorKey: "status",
//         header: "Status",
//         Cell: ({ row }) => {
//           const name = row.original.status?.[0]?.name ?? "";
//           return <OrdersStatus name={name} />;
//         },
//       },
//       {
//         accessorKey: "date",
//         header: "Date",
//       },
//     ],
//     []
//   );

//   if (isLoading || !Array.isArray(orders)) {
//     return <FuseLoading />;
//   }

//   return (
//     <divs
//       className="flex flex-col flex-auto shadow-1 rounded-t-lg overflow-hidden rounded-b-0 w-full h-full "
//       elevation={0}
//     >
//       <DataTable
//         data={orders}
//         columns={columns}
//         renderRowActionMenuItems={({ closeMenu, row, table }) => [
//           <MenuItem
//             key={0}
//             onClick={() => {
//               removeOrders([row.original?.id]);
//               closeMenu();
//               table.resetRowSelection();
//             }}
//           >
//             <ListItemIcon>
//               <FuseSvgIcon className="text-dry">
//                 heroicons-outline:trash
//               </FuseSvgIcon>
//             </ListItemIcon>
//             Delete
//           </MenuItem>,
//         ]}
//         renderTopToolbarCustomActions={({ table }) => {
//           const { rowSelection } = table.getState();
//           if (Object.keys(rowSelection).length === 0) return null;

//           return (
//             <Button
//               variant="contained"
//               size="small"
//               onClick={() => {
//                 const sel = table.getSelectedRowModel().rows;
//                 removeOrders(sel.map((r) => r.original?.id));
//                 table.resetRowSelection();
//               }}
//               className="flex shrink min-w-36 ltr:mr-8 rtl:ml-8"
//               color="secondary"
//             >
//               <FuseSvgIcon size={16}>heroicons-outline:trash</FuseSvgIcon>
//               <span className="hidden sm:flex mx-8">Delete selected items</span>
//             </Button>
//           );
//         }}
//       />
//     </divs>
//   );
// }

// export default OrdersTable;
